/**
 * @name useTotemInterface
 * @description Hook personalizado para gerenciar a lógica da interface do totem com database-driven
 * @author Keven Adan Bezerra | kevenadanb
 */

import { IProdutoCarrinho, useCarrinho } from "@/contexts/CarrinhoContext";
import { useProdutoContext } from "@/contexts/ProdutoContext";
import { useUserContext } from "@/contexts/UserContext";
import { useAlert } from "@/hooks/useAlert";
import { useLoading } from "@/hooks/useLoading";
import { useTotemServices } from "@/services/supabase/entities/useTotemServices";
import {
  IItemVenda,
  IVendaResult,
} from "@/services/supabase/functions/VendaFunctionsService";
import { useCallback, useMemo, useState } from "react";

interface ModalRestricao {
  open: boolean;
  produto: any;
  restricoes: string[];
}

export const useTotemInterface = () => {
  const { alunoAtual } = useUserContext();
  const {
    itens,
    adicionarItem,
    removerItem,
    limparCarrinho,
    total,
    quantidadeTotal,
  } = useCarrinho();

  const { categorias, produtosFiltrados, setCategoriaAtiva } =
    useProdutoContext();

  const { showSuccess, showError, showWarning } = useAlert();
  const { withGlobalLoading } = useLoading();

  // ✨ Serviços do totem com restrições e vendas database-driven
  const totemServices = useTotemServices();

  const [categoriaAtiva, setCategoriaAtivaSelecionada] = useState(
    categorias[0]?.id || null,
  );

  // Estado para controlar a modal de restrições
  const [modalRestricao, setModalRestricao] = useState<ModalRestricao>({
    open: false,
    produto: null,
    restricoes: [],
  });

  // Handler para mudança de categoria - otimizado com useCallback
  const handleCategoriaChange = useCallback(
    (categoriaId: number) => {
      setCategoriaAtivaSelecionada(categoriaId);
      setCategoriaAtiva(categoriaId);
    },
    [setCategoriaAtiva],
  );

  // ✨ Handler para adicionar produto com verificação de restrições database-driven
  const handleAdicionarProduto = useCallback(
    async (produto: any) => {
      if (!alunoAtual?.id) {
        showError("Aluno não identificado");
        return false;
      }

      try {
        // Verificar restrições alimentares no banco de dados
        console.log("🔍 Verificando restrições para produto:", produto.nome);

        const verificacaoRestricoes =
          await totemServices.restricoes.verificarProdutos(alunoAtual.id, [
            produto.id,
          ]);

        if (!verificacaoRestricoes.permitido) {
          console.warn(
            "⚠️ Produto tem restrições:",
            verificacaoRestricoes.motivo,
          );

          // Abrir modal de restrição alimentar com detalhes
          setModalRestricao({
            open: true,
            produto: produto,
            restricoes: verificacaoRestricoes.restricoes.map(
              (r) => r.observacoes || "Restrição não especificada",
            ),
          });

          showWarning(
            `❌ ${produto.nome} não pode ser adicionado: ${verificacaoRestricoes.motivo}`,
          );
          return false;
        }

        // Produto liberado - adicionar ao carrinho
        adicionarItem({
          id: produto.id,
          nome: produto.nome,
          preco: produto.preco || produto.valor,
          imagem: produto.imagem,
        });

        console.log("✅ Produto adicionado ao carrinho:", produto.nome);
        return true;
      } catch (error) {
        console.error("❌ Erro ao verificar restrições:", error);

        // Fail-closed: sem validação confiável, não permite adicionar ao carrinho.
        showError(
          "Não foi possível validar restrições alimentares. Produto não adicionado.",
        );
        return false;
      }
    },
    [
      adicionarItem,
      alunoAtual,
      totemServices.restricoes,
      showError,
      showWarning,
    ],
  );

  // Handler para fechar modal de restrição
  const handleFecharModalRestricao = useCallback(() => {
    setModalRestricao({
      open: false,
      produto: null,
      restricoes: [],
    });
  }, []);

  // Handler para remover produto
  const handleRemoverProduto = useCallback(
    (produtoId: number) => {
      removerItem(produtoId);
    },
    [removerItem],
  );

  // ✨ Função para finalizar compra com database-driven completo
  const handleFinalizarCompra = useCallback(async () => {
    if (!alunoAtual?.id) {
      showError("Aluno não identificado");
      return false;
    }

    if (itens.length === 0) {
      showError("Carrinho vazio");
      return false;
    }

    return await withGlobalLoading(async () => {
      try {
        console.log("🛒 Finalizando compra:", {
          aluno: alunoAtual.nome,
          itens: itens.length,
          total: total,
        });

        // Converter itens do carrinho para formato da função de venda
        const itensVenda: IItemVenda[] = itens.map((item) => ({
          id_produto: item.id,
          quantidade: item.quantidade,
          valor_unitario: item.preco,
        }));

        // Processar venda completa com verificação de restrições
        const resultadoVenda =
          (await totemServices.vendaFunctions.processarComRestricoes(
            alunoAtual.id,
            itensVenda,
            0, // desconto
            "Venda Totem",
          )) as IVendaResult;

        if (!resultadoVenda.success) {
          console.error("❌ Erro na venda:", resultadoVenda.message);
          showError(resultadoVenda.message);
          return false;
        }

        // Sucesso na venda
        console.log("✅ Venda processada:", resultadoVenda);

        // Limpar carrinho
        limparCarrinho();

        showSuccess(
          `🎉 Venda ${resultadoVenda.numero_venda || "#"} realizada com sucesso!`,
        );

        return {
          sucesso: true,
          vendaId: resultadoVenda.id_venda,
          numeroVenda: resultadoVenda.numero_venda,
          valorFinal: resultadoVenda.valor_final || total,
          saldoRestante: resultadoVenda.saldo_restante,
        };
      } catch (error) {
        console.error("❌ Erro ao finalizar compra:", error);
        showError(
          `Erro ao processar compra: ${error instanceof Error ? error.message : "Erro desconhecido"}`,
        );
        return false;
      }
    });
  }, [
    alunoAtual,
    itens,
    total,
    totemServices.vendaFunctions,
    limparCarrinho,
    showSuccess,
    showError,
    withGlobalLoading,
  ]);

  // Função para obter quantidade no carrinho de um produto específico
  const getQuantidadeNoCarrinho = useCallback(
    (produtoId: number): number => {
      return (
        itens.find((item: IProdutoCarrinho) => item.id === produtoId)
          ?.quantidade || 0
      );
    },
    [itens],
  );

  // Verificações de saldo - memoizadas para performance
  const podeFinalizarCompra = useMemo(() => {
    return itens.length > 0 && total <= (alunoAtual?.saldo || 0);
  }, [itens.length, total, alunoAtual?.saldo]);

  const saldoInsuficiente = useMemo(() => {
    return total > (alunoAtual?.saldo || 0);
  }, [total, alunoAtual?.saldo]);

  // Estatísticas do carrinho - memoizadas
  const estatisticasCarrinho = useMemo(() => {
    return {
      totalItens: quantidadeTotal,
      valorTotal: total,
      temItens: itens.length > 0,
      saldoRestante: (alunoAtual?.saldo || 0) - total,
      percentualGasto: alunoAtual?.saldo
        ? Math.min((total / alunoAtual.saldo) * 100, 100)
        : 0,
    };
  }, [quantidadeTotal, total, itens.length, alunoAtual?.saldo]);

  // ✨ Verificação em tempo real se há restrições nos produtos do carrinho
  const verificarRestricoesCarrinho = useCallback(async () => {
    if (!alunoAtual?.id || itens.length === 0) {
      return { temRestricoes: false, produtosRestringidos: [] };
    }

    try {
      const produtoIds = itens.map((item) => item.id);
      const verificacao = await totemServices.restricoes.verificarProdutos(
        alunoAtual.id,
        produtoIds,
      );

      return {
        temRestricoes: !verificacao.permitido,
        produtosRestringidos: verificacao.restricoes,
        motivo: verificacao.motivo,
      };
    } catch (error) {
      console.error("❌ Erro ao verificar restrições do carrinho:", error);
      // Fail-closed: err on the side of caution — se não conseguiu verificar, reporta restrição
      return {
        temRestricoes: true,
        produtosRestringidos: [],
        motivo: "Não foi possível verificar restrições alimentares",
      };
    }
  }, [alunoAtual?.id, itens, totemServices.restricoes]);

  return {
    // Estado
    alunoAtual,
    categoriaAtiva,
    categorias,
    produtosFiltrados,
    itens,

    // Handlers
    handleCategoriaChange,
    handleAdicionarProduto,
    handleRemoverProduto,
    handleFinalizarCompra, // ✨ Novo handler database-driven
    limparCarrinho,

    // Utilities
    getQuantidadeNoCarrinho,
    verificarRestricoesCarrinho, // ✨ Nova função de verificação

    // Validações
    podeFinalizarCompra,
    saldoInsuficiente,

    // Estatísticas
    estatisticasCarrinho,

    // Modal de restrições
    modalRestricao,
    handleFecharModalRestricao,

    // ✨ Context dos serviços do totem
    totemServices,
  };
};

export default useTotemInterface;
